import type { ComboboxItem } from "@mantine/core";
import {
  Button,
  Checkbox,
  Divider,
  Grid,
  ScrollArea,
  Select,
  Text,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { StyledCheckbox } from "src/components/shared/_FormInputs/CheckboxGroup/CheckboxGroup.styled";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import { useGetApi, usePostApi } from "src/hooks";
import { ButtonsContainer } from "src/pages/Home/Home.styled";
import { InputStyledWrapper } from "src/pages/Settings/YachtSetup/VesselParticulars/YachtSetup.styled";
import { PageContainer } from "src/styled";
import {
  mantineDateOnChange,
  toMantineDateString,
} from "src/utils/mantineDates";

interface SubResponse {
  activesubs: number;
  checklists: boolean;
  logs: Array<{
    lbname: string;
    lbid: string;
  }>;
  subs: string[];
}

const mapper = (data: any): SubResponse => {
  return data.data;
};

const EditSubscription = () => {
  const { control, handleSubmit } = useForm();
  const { post } = usePostApi();

  const [type, setType] = useState("");

  const { yachtid, clientid } = useParams();

  const onSubmit = async (data: any) => {
    const startDate = data.start ? new Date(data.start).toDateString() : null;
    const endDate = data.end ? new Date(data.end).toDateString() : null;

    const params = {
      clientid,
      yachtid,
      start: startDate,
      end: endDate,
      checklists: !!data.checklists,
      type: data.type === "Continuous" ? "CNT" : "INT",
    };
    await post({
      request: "done-add-sub",
      body: { params },
      onSuccess: () => {},
    });
  };

  const { data } = useGetApi({
    request: "add-sub",
    body: { yachtid, clientid },
    mapper,
  });

  const types: ComboboxItem[] = [
    { value: "Continuous", label: "Continuous" },
    { value: "Start / End", label: "Start / End" },
  ];

  const checkBoxList = data?.logs ? data?.logs : [];

  return (
    <PageContainer>
      <Breadcrumbs border={false} />
      <PageContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid h={"600px"}>
            <Grid.Col span={6}>
              <Divider my="xs" label="Details" />
              <InputStyledWrapper mb={30}>
                <Text size={"sm"}>Type</Text>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      w={"50%"}
                      data={types}
                      required
                      onChange={value => {
                        field.onChange(value);
                        setType(value || "");
                      }}
                    />
                  )}
                />
              </InputStyledWrapper>
              {type === "Start / End" && (
                <>
                  <InputStyledWrapper mb={30}>
                    <Text size={"sm"}>Start Date</Text>
                    <Controller
                      name="start"
                      control={control}
                      render={({ field }) => (
                        <DatePickerInput
                          dropdownType="modal"
                          placeholder="Pick start date"
                          value={toMantineDateString(field.value)}
                          onChange={mantineDateOnChange(field.onChange)}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      )}
                    />
                  </InputStyledWrapper>
                  <InputStyledWrapper mb={30}>
                    <Text size={"sm"}>End Date</Text>
                    <Controller
                      name="end"
                      control={control}
                      render={({ field }) => (
                        <DatePickerInput
                          dropdownType="modal"
                          placeholder="Pick end date"
                          value={toMantineDateString(field.value)}
                          onChange={mantineDateOnChange(field.onChange)}
                          onBlur={field.onBlur}
                          name={field.name}
                        />
                      )}
                    />
                  </InputStyledWrapper>
                </>
              )}
              <InputStyledWrapper>
                <Controller
                  name="checklists"
                  control={control}
                  render={({ field }) => (
                    <StyledCheckbox
                      {...field}
                      label={"Checklists"}
                      value={"checklists"}
                      labelPosition="right"
                      onChange={e => field.onChange(e.target.checked)}
                    />
                  )}
                />
              </InputStyledWrapper>
            </Grid.Col>
            <Grid.Col span={6}>
              <Divider my="xs" label="Logbooks" />
              <ScrollArea h={500}>
                <InputStyledWrapper mb={30}>
                  <Controller
                    name="list"
                    control={control}
                    defaultValue={[]}
                    render={({ field }) => (
                      <div>
                        {checkBoxList.map(item => (
                          <div
                            key={item.lbid}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}>
                            <Checkbox
                              style={{ marginRight: 10 }}
                              value={item.lbid}
                              checked={field.value.includes(item.lbid)}
                              onChange={e => {
                                const checked = e.target.checked;
                                field.onChange(
                                  checked
                                    ? [...field.value, item.lbid]
                                    : field.value.filter(
                                        (id: string) => id !== item.lbid,
                                      ),
                                );
                              }}
                            />
                            <label style={{ flex: 1, marginRight: "8px" }}>
                              {item.lbname}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                </InputStyledWrapper>
              </ScrollArea>
            </Grid.Col>
          </Grid>
          <ButtonsContainer
            style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="outline">Cancel</Button>
            <Button type="submit">Submit</Button>
          </ButtonsContainer>
        </form>
      </PageContainer>
    </PageContainer>
  );
};

export default EditSubscription;
